<script setup>
// SZ — Card de nota de Ligação de Voz na sidebar do contato.
// Substitui o ContactNoteItem.vue padrão quando a nota é gerada pelo
// twilio-voice-v2 (texto começando com **Ligação de Voz**).
// Recebe a "call" cruzada (call_logs + transcriptions) já matchada no parent.
import { ref, computed } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  note: { type: Object, required: true },
  call: { type: Object, default: null }, // null = sem match no banco do twilio
  writtenBy: { type: String, required: true },
});

const isExpanded = ref(false);
const showTranscription = ref(false);

// Fallback: parsear o markdown da nota quando não conseguimos cruzar com
// call_log (notas antigas sem registro no SQLite, telefone divergente, etc).
const parsedFromNote = computed(() => {
  const c = props.note?.content || '';
  if (!c.startsWith('**Ligação de Voz**')) return null;
  const dateM = c.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
  const agentM = c.match(/\*\*Agente:\*\*\s+([^|\n]+)/);
  const durM = c.match(/\*\*Duração:\*\*\s+([^|\n]+)/);
  const destM = c.match(/\*\*Destino:\*\*\s+([^|\n]+)/);
  // Formato antigo: "**Resumo:**\n<...>\n**Transcrição:**\n<...>"
  // Formato novo:   "**Ligação de Voz**\n\n<resumo>" (só resumo após o prefix)
  let summaryText = '';
  const sumOld = c.match(/\*\*Resumo:\*\*\s*\n([\s\S]+?)(?:\n\*\*Transcrição:\*\*|$)/);
  if (sumOld) {
    summaryText = sumOld[1].trim();
  } else {
    const afterPrefix = c.replace(/^\*\*Ligação de Voz\*\*[^\n]*\n+/, '').trim();
    if (afterPrefix) summaryText = afterPrefix;
  }
  const sumM = summaryText ? [null, summaryText] : null;
  // "11min52seg" / "26seg" / "4min17seg" → seconds
  let durSec = 0;
  if (durM) {
    const minM = durM[1].match(/(\d+)\s*min/);
    const secM = durM[1].match(/(\d+)\s*seg/);
    if (minM) durSec += Number(minM[1]) * 60;
    if (secM) durSec += Number(secM[1]);
  }
  let createdAt = null;
  if (dateM) {
    const [, dd, mm, yyyy, hh, mi] = dateM;
    createdAt = `${yyyy}-${mm}-${dd} ${hh}:${mi}:00`;
  }
  // Sem `call`, status real é desconhecido. Inferimos: duração>0 = completed,
  // senão = missed. Suficiente pra colorir o card.
  return {
    agent_name: agentM ? agentM[1].trim() : '',
    duration: durSec,
    to_number: destM ? destM[1].trim() : '',
    transcription_summary: sumM ? sumM[1].trim() : '',
    transcription_segments: [],
    status: durSec > 0 ? 'completed' : 'no-answer',
    created_at: createdAt,
  };
});

// Fonte de verdade: prioriza call_log (banco) > parsing da nota.
const data = computed(() => props.call || parsedFromNote.value || {});

const status = computed(() => {
  const s = data.value?.status;
  if (s === 'completed') return 'completed';
  if (s === 'no-answer' || s === 'busy' || s === 'failed' || s === 'canceled') return 'missed';
  return 'unknown';
});

const statusLabel = computed(() => {
  if (status.value === 'completed') return 'Atendida';
  if (status.value === 'missed') return 'Perdida';
  return '';
});

const agentName = computed(() => data.value?.agent_name || props.writtenBy);
const phoneNumber = computed(() => data.value?.to_number || '');

const durationFmt = computed(() => {
  const sec = Number(data.value?.duration || 0);
  if (!sec) return '';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} min`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')} min`;
});

const dateFmt = computed(() => {
  const raw = data.value?.created_at;
  if (!raw) return '';
  // call_log vem UTC ("2026-05-20 14:29:09"); parseFromNote vem como local
  // (já no fuso do agente). Tratamos como local — evita off-by-3h.
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} às ${hh}:${mi}`;
});

const dataLine = computed(() => {
  if (!dateFmt.value) return '';
  if (status.value === 'completed' && durationFmt.value) {
    return `Data: ${dateFmt.value} - ${durationFmt.value}`;
  }
  return `Data: ${dateFmt.value}`;
});

const summary = computed(() => data.value?.transcription_summary || '');
const segments = computed(() => data.value?.transcription_segments || []);

const hasTranscription = computed(() => segments.value.length > 0 || summary.value);

const toggleExpanded = () => { isExpanded.value = !isExpanded.value; };
</script>

<template>
  <div class="sz-cn-card" :class="`sz-cn-card--${status}`">
    <!-- Header: avatar/status icon + nome + chevron -->
    <div class="sz-cn-header" @click="toggleExpanded">
      <span class="sz-cn-icon-wrap" :class="`sz-cn-icon-wrap--${status}`">
        <!-- phone_success: para status=completed (SVG do Figma com check ao lado) -->
        <svg v-if="status === 'completed'"
             width="16" height="16" viewBox="0 0 16 16" fill="currentColor"
             xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd"
                d="M5.44765 10.5523C8.01152 13.1161 10.3868 13.3969 11.0839 13.4225C11.1845 13.4262 11.3015 13.4103 11.4524 13.3726L11.5017 13.3602C12.2167 13.1815 12.8178 12.7066 13.1584 12.0603C12.5653 11.3643 11.7928 10.8244 10.9463 10.2391L10.0717 11.1137C9.8062 11.3792 9.39413 11.4876 9.01233 11.3332C8.54684 11.145 7.39605 10.615 6.39046 9.60946C5.38488 8.60388 4.85497 7.45308 4.6667 6.98759C4.51228 6.60579 4.62075 6.19372 4.88622 5.92825L5.76301 5.05146C5.74926 5.03124 5.73554 5.01105 5.72185 4.99091C5.15295 4.1539 4.63068 3.3855 3.9524 2.83542C3.29877 3.17736 2.81832 3.78367 2.63787 4.50543L2.62736 4.54749C2.58963 4.69839 2.5737 4.81539 2.5774 4.91604C2.60305 5.61315 2.88378 7.9884 5.44765 10.5523ZM11.0348 14.755C10.0742 14.7196 7.35377 14.344 4.50484 11.4951C1.65592 8.64615 1.28032 5.9257 1.24497 4.96507C1.23485 4.69018 1.28042 4.43778 1.33384 4.22411L1.34435 4.18205C1.64212 2.99098 2.47959 2.00761 3.60804 1.52399C3.91209 1.39369 4.28034 1.41753 4.57146 1.62989C5.53274 2.33108 6.22667 3.35677 6.8177 4.23036C6.88563 4.33077 6.9522 4.42916 7.01766 4.52483C7.28821 4.92021 7.24353 5.45656 6.90005 5.80004L5.99579 6.7043C6.20567 7.16785 6.63385 7.96723 7.33327 8.66665C8.03269 9.36607 8.83208 9.79425 9.29563 10.0041L10.1994 9.1003C10.5432 8.75652 11.0819 8.7112 11.4782 8.98577C11.5474 9.03377 11.6176 9.08215 11.6885 9.13102C12.5859 9.74998 13.5959 10.4466 14.3473 11.4088C14.575 11.7004 14.6088 12.0818 14.473 12.3987C13.9912 13.5229 13.0116 14.3571 11.8251 14.6538L11.7758 14.6661C11.5621 14.7195 11.3097 14.7651 11.0348 14.755ZM11.0972 6.26084C10.8368 6.52119 10.4147 6.52119 10.1544 6.26084L8.74015 4.84663C8.4798 4.58628 8.4798 4.16417 8.74015 3.90382C9.0005 3.64347 9.42261 3.64347 9.68295 3.90382L10.6258 4.84663L12.9828 2.4896C13.2431 2.22926 13.6652 2.22925 13.9256 2.4896C14.1859 2.74995 14.1859 3.17206 13.9256 3.43241L11.0972 6.26084Z"/>
        </svg>
        <!-- phone_missed: para status=missed (phone com X) -->
        <svg v-else-if="status === 'missed'"
             width="16" height="16" viewBox="0 0 16 16" fill="none"
             stroke="currentColor" stroke-width="1.5"
             stroke-linecap="round" stroke-linejoin="round">
          <path fill="currentColor" stroke="none" fill-rule="evenodd" clip-rule="evenodd"
                d="M5.45 10.55C8.01 13.12 10.39 13.4 11.08 13.42C11.18 13.43 11.3 13.41 11.45 13.37L11.5 13.36C12.22 13.18 12.82 12.71 13.16 12.06C12.57 11.36 11.79 10.82 10.95 10.24L10.07 11.11C9.81 11.38 9.39 11.49 9.01 11.33C8.55 11.14 7.4 10.61 6.39 9.61C5.38 8.6 4.85 7.45 4.67 6.99C4.51 6.61 4.62 6.19 4.89 5.93L5.76 5.05C5.75 5.03 5.74 5.01 5.72 4.99C5.15 4.15 4.63 3.39 3.95 2.84C3.3 3.18 2.82 3.78 2.64 4.51L2.63 4.55C2.59 4.7 2.57 4.82 2.58 4.92C2.6 5.61 2.88 7.99 5.45 10.55ZM11.03 14.75C10.07 14.72 7.35 14.34 4.5 11.5C1.66 8.65 1.28 5.93 1.24 4.97C1.23 4.69 1.28 4.44 1.33 4.22L1.34 4.18C1.64 2.99 2.48 2.01 3.61 1.52C3.91 1.39 4.28 1.42 4.57 1.63C5.53 2.33 6.23 3.36 6.82 4.23C6.89 4.33 6.95 4.43 7.02 4.52C7.29 4.92 7.24 5.46 6.9 5.8L5.99 6.7C6.2 7.17 6.63 7.97 7.33 8.67C8.03 9.37 8.83 9.79 9.3 10L10.2 9.1C10.54 8.76 11.08 8.71 11.48 8.99C11.55 9.03 11.62 9.08 11.69 9.13C12.59 9.75 13.6 10.45 14.35 11.41C14.58 11.7 14.61 12.08 14.47 12.4C13.99 13.52 13.01 14.36 11.83 14.65L11.78 14.67C11.56 14.72 11.31 14.77 11.03 14.75Z"/>
          <path d="M9.5 3l3 3M12.5 3l-3 3" />
        </svg>
        <!-- fallback genérico (status unknown) -->
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
                   19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1
                   4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0
                   0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0
                   1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      </span>
      <div class="sz-cn-info">
        <div class="sz-cn-agent-row">
          <Avatar :name="agentName" :size="20" rounded-full />
          <span class="sz-cn-agent">{{ agentName }}</span>
        </div>
        <div v-if="phoneNumber" class="sz-cn-phone">{{ phoneNumber }}</div>
        <div v-if="dataLine" class="sz-cn-data">{{ dataLine }}</div>
      </div>
      <button v-if="hasTranscription" class="sz-cn-chev" @click.stop="toggleExpanded">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="1.5"
             stroke-linecap="round" stroke-linejoin="round"
             :style="{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms' }">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>
    </div>

    <!-- Expanded: resumo + Ler transcrição -->
    <div v-if="isExpanded && hasTranscription" class="sz-cn-body">
      <div v-if="summary">
        <div class="sz-cn-summary-title">Resumo:</div>
        <div class="sz-cn-summary">{{ summary }}</div>
      </div>
      <button v-if="segments.length > 0" class="sz-cn-trans-btn" @click="showTranscription = true">
        Ler transcrição
      </button>
    </div>

    <!-- Modal de transcrição -->
    <Teleport to="body">
      <div v-if="showTranscription" class="sz-trans-backdrop" @click.self="showTranscription = false">
        <div class="sz-trans-card">
          <div class="sz-trans-header">
            <span class="sz-trans-title">Transcrição da ligação</span>
            <button class="sz-trans-close" aria-label="Fechar" @click="showTranscription = false">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>
          <div class="sz-trans-body">
            <div v-for="(seg, i) in segments" :key="i"
                 class="sz-trans-seg"
                 :class="seg.speaker === agentName ? 'sz-trans-seg--agent' : 'sz-trans-seg--client'">
              <div class="sz-trans-seg-head">{{ seg.speaker }}</div>
              <div class="sz-trans-seg-text">{{ seg.text }}</div>
            </div>
            <div v-if="segments.length === 0" class="sz-trans-empty">
              Transcrição vazia.
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.sz-cn-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #FFFFFF;
  border: 1px solid #D4D4D4;
  border-radius: 8px;
  font-family: 'Inter', system-ui, sans-serif;
}
.sz-cn-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}
.sz-cn-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  flex-shrink: 0;
}
.sz-cn-icon-wrap--completed { background: #EEF6F0; color: #5EA500; }
.sz-cn-icon-wrap--missed    { background: #FAE5E5; color: #DC2626; }
.sz-cn-icon-wrap--unknown   { background: #F5F5F5; color: #7A7A7A; }
.sz-cn-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.sz-cn-agent-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
}
.sz-cn-agent {
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-cn-phone {
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: #A1A1A1;
}
.sz-cn-data {
  font-weight: 400;
  font-size: 10px;
  line-height: 16px;
  color: #525252;
}
.sz-cn-chev {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #000000;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.sz-cn-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sz-cn-summary-title {
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  color: #262626;
}
.sz-cn-summary {
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #262626;
  white-space: pre-wrap;
}
.sz-cn-trans-btn {
  align-self: flex-start;
  background: #EEF6F0;
  color: #236D23;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
}
.sz-cn-trans-btn:hover {
  background: #DCEBE0;
}

/* Modal transcrição */
.sz-trans-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', system-ui, sans-serif;
}
.sz-trans-card {
  background: #FFFFFF;
  border-radius: 12px;
  width: 560px;
  max-width: 94vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,.45);
}
.sz-trans-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #E5E5E5;
}
.sz-trans-title {
  font-weight: 600;
  font-size: 16px;
  color: #262626;
}
.sz-trans-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #525252;
}
.sz-trans-body {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
}
.sz-trans-seg {
  padding: 8px 12px;
  border-radius: 8px;
}
.sz-trans-seg--agent  { background: rgba(255,196,108,0.18); }
.sz-trans-seg--client { background: rgba(132,187,255,0.18); }
.sz-trans-seg-head {
  font-weight: 600;
  font-size: 12px;
  color: #525252;
  margin-bottom: 2px;
}
.sz-trans-seg-text {
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #262626;
}
.sz-trans-empty {
  color: #7A7A7A;
  font-size: 13px;
  text-align: center;
  padding: 24px;
}
</style>

<style>
/* Dark mode (não-scoped pra fugir do bug Vue scoped + :global) */
.dark .sz-cn-card {
  background: #17181B;
  border-color: #26282B;
}
.dark .sz-cn-icon-wrap--completed { background: #194121; color: #97C79D; }
.dark .sz-cn-icon-wrap--missed    { background: #501717; color: #E86868; }
.dark .sz-cn-icon-wrap--unknown   { background: #26282B; color: #9799A0; }
.dark .sz-cn-agent     { color: #FFFFFF; }
.dark .sz-cn-phone     { color: #92939A; }
.dark .sz-cn-data      { color: #A1A1A1; }
.dark .sz-cn-chev      { color: #FFFFFF; }
.dark .sz-cn-summary-title { color: #FFFFFF; }
.dark .sz-cn-summary       { color: #E5E5E5; }
.dark .sz-cn-trans-btn { background: #194121; color: #97C79D; }
.dark .sz-cn-trans-btn:hover { background: #234E2C; }

.dark .sz-trans-card { background: #17181B; }
.dark .sz-trans-header { border-bottom-color: #26282B; }
.dark .sz-trans-title { color: #FFFFFF; }
.dark .sz-trans-close { color: #9799A0; }
.dark .sz-trans-seg--agent  { background: rgba(255,196,108,0.12); }
.dark .sz-trans-seg--client { background: rgba(132,187,255,0.12); }
.dark .sz-trans-seg-head { color: #9799A0; }
.dark .sz-trans-seg-text { color: #FFFFFF; }
.dark .sz-trans-empty { color: #9799A0; }
</style>
